import React from 'react';
import { Button, Typography, Space, Card } from 'antd';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { 
  RocketOutlined, 
  BulbOutlined, 
  TeamOutlined,
  RobotOutlined,
  SearchOutlined,
  CodeOutlined,
  ExperimentOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/userSlice';
import Navbar from '../components/layout/Navbar';
import landAim from '../assets/land_aim.jpg';
import step1 from '../assets/step1.jpg';
import step2 from '../assets/step2.jpg';
import step3 from '../assets/step3.jpg';
import step4 from '../assets/step4.jpg';
import step5 from '../assets/step5.jpg';

const { Title, Paragraph } = Typography;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  width: 100%;
  position: relative;
  overflow-x: hidden;

  &::before,
  &::after {
    content: '';
    position: fixed;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
    z-index: 0;
    pointer-events: none;
  }

  &::before {
    top: -100px;
    right: -100px;
    animation: ${float} 6s ease-in-out infinite;
  }

  &::after {
    bottom: -100px;
    left: -100px;
    animation: ${float} 8s ease-in-out infinite;
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  padding-top: 80px;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
`;

const Hero = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0 4rem;
  gap: 3rem;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    margin: 1rem 0 2rem;
    gap: 1.5rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 1rem;
  }
`;

const HeroImage = styled.div`
  flex: 1;
  max-width: 450px;
  min-width: auto;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(37, 99, 235, 0.2);
  overflow: hidden;
  transform: perspective(1000px) rotateY(-5deg);
  transition: all 0.5s ease;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover {
    transform: perspective(1000px) rotateY(0deg);
    box-shadow: 0 30px 60px rgba(37, 99, 235, 0.3);
    
    &::before {
      opacity: 1;
    }
    
    img {
      transform: scale(1.05);
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const StyledTitle = styled(Title)`
  &.ant-typography {
    color: #1e293b;
    margin-bottom: 1.5rem;
    font-size: 4rem !important;
    line-height: 1.1;
    font-weight: 800;
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: 768px) {
      font-size: 2.5rem !important;
    }
  }
`;

const Features = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 3rem 0;
  animation: ${fadeIn} 1s ease-out;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
    border-radius: 50%;
    top: -50px;
    right: -50px;
    z-index: -1;
    animation: ${float} 7s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled(Card)`
  &.ant-card {
    height: 100%;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      border-color: rgba(37, 99, 235, 0.2);
      background: rgba(255, 255, 255, 0.95);
    }
    
    .ant-card-head {
      padding: 16px 16px 0;
    }
    
    .ant-card-head-title {
      font-size: 1.5rem;
      color: #1e293b;
      font-weight: 600;
    }
    
    .ant-card-body {
      padding: 16px;
    }
    
    .anticon {
      font-size: 2rem;
      color: #2563eb;
      margin-bottom: 1rem;
      transition: all 0.3s ease;
    }
    
    &:hover .anticon {
      transform: scale(1.1) rotate(5deg);
      color: #1d4ed8;
    }
  }
`;

const AdvantageSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  margin: 2rem 0;
`;

const StepsSection = styled.div`
  padding: 80px 0;
  background: #f8f9fa;
  overflow: hidden;
`;

const StepContent = styled.div`
  flex: 1;
  max-width: 600px;
  padding: 40px 80px;
  position: relative;
  z-index: 1;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 80px;
  position: relative;
  margin: 0 -80px 60px -80px;
  
  &:nth-child(odd) {
    flex-direction: row-reverse;
    
    .step-content {
      background: linear-gradient(to left, rgba(24, 144, 255, 0.05) 0%, transparent 50%);
      padding-right: -120px;
      margin-right: 40px;
    }
  }
  
  &:nth-child(even) {
    .step-content {
      background: linear-gradient(to right, rgba(24, 144, 255, 0.05) 0%, transparent 50%);
      padding-left: -120px;
      margin-left: 40px;
    }
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const StepIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #1890ff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  
  .anticon {
    font-size: 24px;
    color: white;
  }
`;

const StepImage = styled.img`
  width: 500px;
  height: 240px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 0;
  flex-shrink: 0;
`;

const StepTitle = styled(Title)`
  &.ant-typography {
    font-size: 2rem;
    margin: 0;
  }
`;

const StepDescription = styled(Paragraph)`
  &.ant-typography {
    font-size: 1.1rem;
    color: rgba(0, 0, 0, 0.65);
    margin: 24px 0;
    line-height: 1.6;
    max-width: 540px;
  }
`;

const ActionButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  font-size: 1.1rem;
  border-radius: 8px;
  
  &.ant-btn-primary {
    background: #1890ff;
    border-color: #1890ff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
    
    &:hover {
      background: #40a9ff;
      border-color: #40a9ff;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(24, 144, 255, 0.3);
    }
  }
`;

const Footer = styled.footer`
  background: linear-gradient(180deg, rgba(240, 247, 255, 0) 0%, rgba(240, 247, 255, 0.8) 100%);
  padding: 1rem 0;
  margin-top: 2rem;
  width: 100%;
  text-align: center;
`;

const Copyright = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const StepContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 80px;
  position: relative;
  overflow: hidden;
`;

const LandingView: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const handleGetStarted = () => {
    navigate('/app');
  };

  const handleFooterLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // 暂时导航到认证页面,后续可以改为对应的页面
    navigate('/auth');
  };

  const featureItems = [
    {
      title: "专业指导",
      icon: <RocketOutlined />,
      description: "系统化的创业指导，让创业过程更加清晰明确。"
    },
    {
      title: "智能辅助",
      icon: <RobotOutlined />,
      description: "AI助手全程指导，解答疑惑，提供建议。"
    },
    {
      title: "资源对接",
      icon: <TeamOutlined />,
      description: "连接优质创业资源，助力快速成长。"
    }
  ];

  const steps = [
    {
      icon: <SearchOutlined />,
      title: "问题定义", 
      description: "通过深入调研和用户访谈，找到真实存在的用户痛点。确保创业方向正确，避免盲目投入。",
      image: step1
    },
    {
      icon: <ExperimentOutlined />,
      title: "方案验证",
      description: "设计并测试解决方案，通过快速原型验证用户反馈。让产品更贴近用户需求，提高成功率。", 
      image: step2
    },
    {
      icon: <CodeOutlined />,
      title: "MVP打造",
      description: "高效构建最小可行产品，专注核心功能开发。快速推出产品初版，收集真实用户反馈。",
      image: step3
    },
    {
      icon: <RocketOutlined />,
      title: "启动实验",
      description: "执行产品发布计划，验证获客策略。通过小规模测试，优化产品和运营方案。",
      image: step4
    },
    {
      icon: <LineChartOutlined />,
      title: "规模增长",
      description: "建立可持续的增长机制，持续优化用户体验。通过数据分析，实现产品的规模化发展。",
      image: step5
    }
  ];

  return (
    <Container>
      <Navbar />
      <MainContent>
        <Hero>
          <HeroContent>
            <StyledTitle level={1}>
              轻松上路，创业无忧
            </StyledTitle>
            <Paragraph style={{ 
              fontSize: '1.25rem', 
              color: '#475569', 
              marginBottom: '2.5rem',
              lineHeight: 1.6
            }}>
              从创意到落地，来创业（LetStartUp）帮你避开创业陷阱，逐步实现目标，稳步引导你走向清晰、高效、低成本的创业之路。
            </Paragraph>
            <Space size="middle">
              <Button 
                type="primary" 
                size="large"
                onClick={handleGetStarted}
                style={{
                  height: '48px',
                  padding: '0 2rem',
                  fontSize: '1.1rem',
                  borderRadius: '24px',
                  background: '#2563eb'
                }}
              >
                开启创业之路
              </Button>
            </Space>
          </HeroContent>
          <HeroImage>
            <img src={landAim} alt="创业规划" />
          </HeroImage>
        </Hero>

        <Features>
          {featureItems.map((item, index) => (
            <FeatureCard
              key={index}
              title={item.title}
              bordered={false}
            >
              {item.icon}
              <Paragraph style={{ fontSize: '1.1rem', color: '#475569' }}>
                {item.description}
              </Paragraph>
            </FeatureCard>
          ))}
        </Features>

        <StepsSection>
          <StepContainer>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepContent className="step-content">
                  <StepHeader>
                    <StepIcon>{step.icon}</StepIcon>
                    <StepTitle level={2}>{step.title}</StepTitle>
                  </StepHeader>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
                <StepImage src={step.image} alt={step.title} />
              </Step>
            ))}
          </StepContainer>
        </StepsSection>

        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <Title level={2} style={{ marginBottom: '2rem' }}>
            现在就开始你的创业之旅
          </Title>
          <Space size="large">
            <Button 
              type="primary" 
              size="large"
              onClick={() => navigate('/auth?tab=register')}
              style={{
                height: '48px',
                padding: '0 2rem',
                fontSize: '1.1rem',
                borderRadius: '24px',
                background: '#2563eb'
              }}
            >
              免费注册
            </Button>
            <Button 
              size="large"
              onClick={() => navigate('/auth?tab=login')}
              style={{
                height: '48px',
                padding: '0 2rem',
                fontSize: '1.1rem',
                borderRadius: '24px'
              }}
            >
              立即登录
            </Button>
          </Space>
        </div>
      </MainContent>

      <Footer>
        <Copyright>
          © {new Date().getFullYear()} 来创业（LetStartUp）. All rights reserved.
        </Copyright>
      </Footer>
    </Container>
  );
};

export default LandingView; 